import { useEffect, useRef, useState } from "react";
import {
  CheckCircle2,
  Circle,
  FileSpreadsheet,
  FileText,
  FolderUp,
  Link2,
  Loader2,
  Plus,
  Plug,
  X,
} from "lucide-react";

import netsuiteLogo from "@/assets/logos/netsuite-logo.webp";
import tallyLogo from "@/assets/logos/tally-logo.png";
import zohoBooksLogo from "@/assets/logos/zohobooks-logo.jpeg";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";

const DEFAULT_IMPORTED_COUNT = 1284;
const SAP_SYNC_DURATION_MS = 3000;
const sapLogo = "/sap-logo.svg";
const oracleLogo = "/oracle-logo.svg";

const CHECKLIST_ITEMS = [
  { id: "gl", label: "Fetching GL entries..." },
  { id: "vendor", label: "Loading vendor master..." },
  { id: "po", label: "Importing PO register..." },
];

const INTEGRATIONS = [
  {
    id: "sap",
    name: "SAP",
    status: "connected",
    logoSrc: sapLogo,
  },
  {
    id: "oracle",
    name: "Oracle",
    status: "not_connected",
    logoSrc: oracleLogo,
    logoStyle: { mixBlendMode: "multiply" },
  },
  {
    id: "netsuite",
    name: "NetSuite",
    status: "not_connected",
    logoSrc: netsuiteLogo,
  },
  {
    id: "tally",
    name: "Tally",
    status: "not_connected",
    logoSrc: tallyLogo,
    logoStyle: { mixBlendMode: "multiply" },
  },
  {
    id: "zoho-books",
    name: "Zoho Books",
    status: "not_connected",
    logoSrc: zohoBooksLogo,
  },
  {
    id: "others",
    name: "Custom / Others",
    status: "not_connected",
    Icon: Plug,
  },
];

const getChecklistState = (index, progress) => {
  const completionThreshold = (index + 1) * 30;
  const previousThreshold = index === 0 ? 0 : index * 30;

  if (progress >= completionThreshold) {
    return "complete";
  }

  if (progress >= previousThreshold) {
    return "current";
  }

  return "pending";
};

export default function DataSourceModal({
  defaultOpen = false,
  preferredTab = "manual",
  triggerLabel = "Add Data Source",
  autoCloseOnSapSuccess = false,
  onRevealUploadArea,
  onSapImport,
}) {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("manual");
  const [expandedCard, setExpandedCard] = useState(null);
  const [syncPhase, setSyncPhase] = useState("idle");
  const [progress, setProgress] = useState(0);
  const [importedCount, setImportedCount] = useState(DEFAULT_IMPORTED_COUNT);
  const [syncError, setSyncError] = useState("");

  const progressIntervalRef = useRef(null);
  const resetTimeoutRef = useRef(null);
  const didAutoOpenRef = useRef(false);

  const clearTimers = () => {
    if (progressIntervalRef.current) {
      window.clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }

    if (resetTimeoutRef.current) {
      window.clearTimeout(resetTimeoutRef.current);
      resetTimeoutRef.current = null;
    }
  };

  const resetState = (nextTab = "manual") => {
    clearTimers();
    setActiveTab(nextTab);
    setExpandedCard(null);
    setSyncPhase("idle");
    setProgress(0);
    setImportedCount(DEFAULT_IMPORTED_COUNT);
    setSyncError("");
  };

  const scheduleReset = () => {
    if (resetTimeoutRef.current) {
      window.clearTimeout(resetTimeoutRef.current);
    }

    resetTimeoutRef.current = window.setTimeout(() => {
      resetState();
    }, 180);
  };

  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, []);

  useEffect(() => {
    if (defaultOpen && !didAutoOpenRef.current) {
      didAutoOpenRef.current = true;
      clearTimers();
      setActiveTab(preferredTab);
      setExpandedCard(null);
      setSyncPhase("idle");
      setProgress(0);
      setImportedCount(DEFAULT_IMPORTED_COUNT);
      setSyncError("");
      setOpen(true);
    }
  }, [defaultOpen, preferredTab]);

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen && syncPhase === "syncing") {
      return;
    }

    setOpen(nextOpen);

    if (!nextOpen) {
      scheduleReset();
    }
  };

  const openModal = (nextTab = preferredTab) => {
    resetState(nextTab);
    setOpen(true);
  };

  const closeAndReveal = (source) => {
    setOpen(false);
    scheduleReset();
    window.setTimeout(() => {
      onRevealUploadArea?.(source);
    }, 180);
  };

  const handleManualSelection = () => {
    closeAndReveal("manual");
  };

  const startSapSync = async () => {
    if (syncPhase === "syncing") {
      return;
    }

    clearTimers();
    setExpandedCard(null);
    setSyncPhase("syncing");
    setProgress(0);
    setSyncError("");
    setImportedCount(DEFAULT_IMPORTED_COUNT);

    const startedAt = Date.now();

    progressIntervalRef.current = window.setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const nextProgress = Math.min(
        100,
        Math.round((elapsed / SAP_SYNC_DURATION_MS) * 100)
      );
      setProgress(nextProgress);

      if (nextProgress >= 100) {
        window.clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    }, 120);

    try {
      const [importResult] = await Promise.all([
        Promise.resolve(onSapImport?.()),
        new Promise((resolve) => {
          window.setTimeout(resolve, SAP_SYNC_DURATION_MS);
        }),
      ]);

      setProgress(100);
      setImportedCount(importResult?.importedCount ?? DEFAULT_IMPORTED_COUNT);
      if (autoCloseOnSapSuccess) {
        closeAndReveal("sap");
      } else {
        setSyncPhase("success");
      }
    } catch (error) {
      setProgress(100);
      setSyncError(
        error instanceof Error
          ? error.message
          : "Unable to import sample SAP data right now."
      );
      setSyncPhase("error");
    } finally {
      if (progressIntervalRef.current) {
        window.clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    }
  };

  const checklistStates = CHECKLIST_ITEMS.map((item, index) => ({
    ...item,
    state: getChecklistState(index, progress),
  }));

  const renderManualTab = () => {
    return (
      <div className="space-y-6">
        <button
          type="button"
          onClick={handleManualSelection}
          className="group w-full rounded-[28px] border border-dashed border-slate-300 bg-slate-50/80 p-8 text-left transition-all duration-200 hover:scale-[1.01] hover:border-[#6FA8F7] hover:bg-blue-50/70 hover:shadow-[0_20px_45px_-30px_rgba(111,168,247,0.5)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6FA8F7]/60 focus-visible:ring-offset-2"
          data-testid="manual-upload-zone"
        >
          <div className="flex flex-col items-center gap-5 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white text-[#062B55] shadow-sm ring-1 ring-slate-200">
              <FolderUp size={28} strokeWidth={1.8} />
            </div>

            <div className="space-y-2">
              <p className="text-lg font-semibold tracking-tight text-[#062B55]">
                Drag and drop your audit files
              </p>
              <p className="text-sm text-slate-500">
                Upload your source exports from the current page below.
              </p>
            </div>

            <p className="text-sm text-slate-500">
              Or{" "}
              <span className="font-semibold text-[#0a4aa6] transition-colors group-hover:text-[#062B55]">
                click to browse
              </span>
            </p>

            <div className="flex flex-wrap items-center justify-center gap-3">
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600">
                <FileSpreadsheet size={14} className="text-emerald-600" />
                CSV
              </div>
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600">
                <FileSpreadsheet size={14} className="text-slate-500" />
                XLSX
              </div>
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600">
                <FileText size={14} className="text-rose-500" />
                PDF
              </div>
            </div>
          </div>
        </button>

        <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-500">
          <span>Supported formats: CSV, XLSX, PDF</span>
          <span className="font-medium text-slate-600">Max file size: 50MB</span>
        </div>
      </div>
    );
  };

  const renderIntegrationCard = (integration) => {
    const isConnected = integration.status === "connected";
    const isExpanded = expandedCard === integration.id;
    const statusBadgeClassName = isConnected
      ? "bg-[#DCFCE7] text-[#16A34A]"
      : "bg-[#F3F4F6] text-[#9CA3AF]";

    const handleClick = () => {
      if (isConnected) {
        startSapSync();
        return;
      }

      setExpandedCard((current) =>
        current === integration.id ? null : integration.id
      );
    };

    return (
      <button
        key={integration.id}
        type="button"
        onClick={handleClick}
        className={`group relative flex h-[176px] flex-col overflow-hidden rounded-2xl border bg-white text-center shadow-[0_8px_24px_-18px_rgba(15,23,42,0.18)] transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6FA8F7]/60 focus-visible:ring-offset-2 ${
          isConnected
            ? "border-[#D1FAE5] hover:border-[#BBF7D0] hover:shadow-[0_16px_32px_-20px_rgba(22,163,74,0.28)]"
            : "border-[#E5E7EB]"
        }`}
        data-testid={`integration-card-${integration.id}`}
      >
        <span
          className={`absolute right-2 top-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ${statusBadgeClassName}`}
        >
          <span
            className={`h-2 w-2 rounded-full ${
              isConnected ? "bg-[#16A34A] animate-pulse" : "bg-[#9CA3AF]"
            }`}
          />
          {isConnected ? "Connected" : "Not connected"}
        </span>

        <div className="relative flex flex-1 items-center justify-center px-6 pt-6">
          <div className="peer flex min-h-[72px] w-full translate-y-[2px] items-center justify-center rounded-xl bg-white p-3">
            {integration.logoSrc ? (
              <img
                src={integration.logoSrc}
                alt={`${integration.name} logo`}
                className="h-[48px] w-[120px] object-contain"
                loading="lazy"
                style={integration.logoStyle}
              />
            ) : (
              <integration.Icon
                size={30}
                strokeWidth={1.8}
                className="text-slate-500"
              />
            )}
          </div>

          <p className="pointer-events-none absolute inset-x-6 bottom-[7px] text-center text-[12px] font-medium leading-4 text-slate-500 opacity-0 transition-all duration-150 translate-y-0.5 peer-hover:translate-y-0 peer-hover:opacity-100">
            {integration.name}
          </p>
        </div>

        <div className="mx-6 mb-2 flex h-[30px] shrink-0 items-center justify-center border-t border-slate-100">
          {isConnected ? (
            <span className="inline-flex rounded-full px-2.5 py-1 leading-none text-[13px] font-semibold text-[#16A34A] transition-all duration-200 ease-out will-change-transform group-hover:-translate-y-0.5 group-hover:bg-[#16A34A]/8 group-hover:text-[#15803D] group-hover:shadow-[0_10px_24px_-18px_rgba(22,163,74,0.8)] group-active:translate-y-0 group-active:scale-[0.98] group-active:bg-[#16A34A]/12">
              Sync now
            </span>
          ) : (
            <span
              className={`leading-none text-[12px] font-medium text-slate-400 transition-all duration-200 ${
                isExpanded ? "translate-y-0 opacity-100" : "translate-y-1 opacity-0"
              }`}
            >
              Coming soon
            </span>
          )}
        </div>
      </button>
    );
  };

  const renderIntegrationTab = () => {
    return (
      <div className="grid grid-cols-3 gap-4">
        {INTEGRATIONS.map((integration) => renderIntegrationCard(integration))}
      </div>
    );
  };

  const renderSyncScreen = () => {
    return (
      <div className="space-y-8 py-4">
        <div className="space-y-3">
          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700 ring-1 ring-emerald-200">
            <Loader2 size={14} className="animate-spin" />
            Secure SAP Sync
          </div>
          <div>
            <h3 className="text-2xl font-semibold tracking-tight text-[#062B55]">
              Syncing data from SAP...
            </h3>
            <p className="mt-2 text-sm text-slate-500">
              We&apos;re importing your connected audit datasets into AuditBuddy.
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <div className="h-3 overflow-hidden rounded-full bg-slate-100">
            <div
              className="h-full rounded-full bg-gradient-to-r from-emerald-500 via-emerald-400 to-teal-400 transition-all duration-200"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex items-center justify-between text-xs font-medium uppercase tracking-[0.16em] text-slate-400">
            <span>Progress</span>
            <span>{progress}%</span>
          </div>
        </div>

        <div className="space-y-3 rounded-[24px] border border-slate-200 bg-slate-50/80 p-5">
          {checklistStates.map((item) => (
            <div
              key={item.id}
              className="flex items-center gap-3 text-sm text-slate-600"
            >
              {item.state === "complete" ? (
                <CheckCircle2 size={18} className="text-emerald-600" />
              ) : item.state === "current" ? (
                <Loader2 size={18} className="animate-spin text-[#0a4aa6]" />
              ) : (
                <Circle size={18} className="text-slate-300" />
              )}
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderSuccessScreen = () => {
    return (
      <div className="flex flex-1 flex-col justify-center py-4">
        <div className="rounded-[28px] border border-emerald-200 bg-emerald-50/70 p-8">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
            <CheckCircle2 size={30} strokeWidth={1.8} />
          </div>

          <div className="mt-6 text-center">
            <h3 className="text-2xl font-semibold tracking-tight text-[#062B55]">
              {`${Number(importedCount || DEFAULT_IMPORTED_COUNT).toLocaleString()} records imported successfully`}
            </h3>
            <p className="mt-2 text-sm text-slate-500">
              Your SAP sample data is now available in the upload workflow below.
            </p>
          </div>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Button
              type="button"
              onClick={closeAndReveal}
              className="rounded-xl bg-[#062B55] px-6 text-white hover:bg-[#0a3d7a]"
              data-testid="sap-view-data-button"
            >
              View Data
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              className="rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50"
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const renderErrorScreen = () => {
    return (
      <div className="flex flex-1 flex-col justify-center py-4">
        <div className="rounded-[28px] border border-rose-200 bg-rose-50/70 p-8">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-rose-100 text-rose-700">
            <X size={28} strokeWidth={1.9} />
          </div>

          <div className="mt-6 text-center">
            <h3 className="text-2xl font-semibold tracking-tight text-[#062B55]">
              SAP sync needs another attempt
            </h3>
            <p className="mt-2 text-sm text-slate-500">
              {syncError || "Unable to import sample SAP data right now."}
            </p>
          </div>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Button
              type="button"
              onClick={startSapSync}
              className="rounded-xl bg-[#062B55] px-6 text-white hover:bg-[#0a3d7a]"
            >
              Retry Sync
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              className="rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50"
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const isSyncing = syncPhase === "syncing";
  const showTabs = syncPhase === "idle";
  const TriggerIcon = triggerLabel.includes("Switch") ? Link2 : Plus;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <Button
        type="button"
        onClick={() => openModal(preferredTab)}
        className="inline-flex items-center gap-2 rounded-xl bg-[#062B55] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_14px_35px_-18px_rgba(6,43,85,0.7)] transition-all duration-200 hover:bg-[#0a3d7a] hover:shadow-[0_18px_38px_-18px_rgba(6,43,85,0.8)]"
        data-testid="data-source-trigger"
      >
        <TriggerIcon size={16} />
        {triggerLabel}
      </Button>

      <DialogContent
        className="h-[100dvh] w-screen max-w-none gap-0 overflow-hidden rounded-none border-0 p-0 shadow-none sm:h-auto sm:max-w-2xl sm:rounded-[28px] sm:border sm:border-slate-200 sm:shadow-[0_24px_80px_-28px_rgba(15,23,42,0.35)] [&>button]:hidden"
        onEscapeKeyDown={(event) => {
          if (isSyncing) {
            event.preventDefault();
          }
        }}
        onPointerDownOutside={(event) => {
          if (isSyncing) {
            event.preventDefault();
          }
        }}
        data-testid="data-source-modal"
      >
        <div className="relative flex h-full flex-col bg-white">
          <button
            type="button"
            onClick={() => handleOpenChange(false)}
            disabled={isSyncing}
            className="absolute right-5 top-5 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Close data source selector"
          >
            <X size={18} />
          </button>

          <div className="flex flex-1 flex-col overflow-hidden">
            <div className="border-b border-slate-200 px-6 pb-5 pt-6 sm:px-8 sm:pb-6 sm:pt-8">
              <div className="pr-12">
                <DialogTitle className="text-2xl font-semibold tracking-tight text-[#062B55]">
                  Select Data Source
                </DialogTitle>
                <DialogDescription className="mt-2 max-w-xl text-sm leading-6 text-slate-500">
                  Choose how you want to import your audit data.
                </DialogDescription>
              </div>

              {showTabs && (
                <div className="mt-6 rounded-2xl bg-slate-100 p-1.5">
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setActiveTab("manual");
                        setExpandedCard(null);
                      }}
                      className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition-all ${
                        activeTab === "manual"
                          ? "bg-white text-[#062B55] shadow-sm"
                          : "text-slate-500 hover:text-slate-700"
                      }`}
                      data-testid="manual-tab-trigger"
                    >
                      <FolderUp size={16} />
                      Manual Upload
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setActiveTab("integrations");
                        setExpandedCard(null);
                      }}
                      className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition-all ${
                        activeTab === "integrations"
                          ? "bg-white text-[#062B55] shadow-sm"
                          : "text-slate-500 hover:text-slate-700"
                      }`}
                      data-testid="integrations-tab-trigger"
                    >
                      <Link2 size={16} />
                      ERP / Integrations
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6 sm:px-8 sm:py-8">
              {syncPhase === "syncing" && renderSyncScreen()}
              {syncPhase === "success" && renderSuccessScreen()}
              {syncPhase === "error" && renderErrorScreen()}
              {syncPhase === "idle" &&
                (activeTab === "manual"
                  ? renderManualTab()
                  : renderIntegrationTab())}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
